/**
 * StockTab.js - Tab de Stock con desglose por tipo+plan y folios expandibles
 *
 * Funcionalidades:
 * - Tabla de stock por tipo y plan
 * - Filas expandibles con lista de folios paginada (100 por página)
 * - Navegación de páginas y salto directo
 * - Copiar página actual al portapapeles
 * - Exportar todos los folios a CSV
 */
import React, { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../../../services/api";

function StockTab({ onRefresh }) {
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  // Estado para folios expandidos
  const [foliosData, setFoliosData] = useState({});
  const [loadingFolios, setLoadingFolios] = useState(false);
  const [goToPageInput, setGoToPageInput] = useState({});

  // Cargar stock detallado
  const loadStock = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/objetos/mkt/stock', { headers });

      if (!response.ok) {
        throw new Error('Error al cargar stock');
      }

      const data = await response.json();
      setStock(data.data);
    } catch (err) {
      console.error('Error cargando stock:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  // Cargar folios para una categoría
  const loadFolios = useCallback(async (tipo, plan, page = 1) => {
    const key = `${tipo}_${plan}`;
    setLoadingFolios(true);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `/api/objetos/mkt/stock/folios?tipo=${tipo}&plan=${plan}&page=${page}&pageSize=100`,
        { headers }
      );

      if (!response.ok) {
        throw new Error('Error al cargar folios');
      }

      const result = await response.json();
      setFoliosData(prev => ({
        ...prev,
        [key]: result.data
      }));
    } catch (err) {
      console.error('Error cargando folios:', err);
      setMensaje({ type: 'danger', text: err.message });
    } finally {
      setLoadingFolios(false);
    }
  }, []);

  // Toggle categoría expandida
  const toggleCategory = async (key, tipo, plan) => {
    if (expandedCategory === key) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(key);
      // Cargar folios si no están cargados
      if (!foliosData[key]) {
        await loadFolios(tipo, plan, 1);
      }
    }
  };

  // Navegar a página
  const goToPage = async (tipo, plan, page) => {
    const key = `${tipo}_${plan}`;
    const current = foliosData[key];
    if (!current) return;

    const newPage = Math.max(1, Math.min(page, current.totalPages));
    if (newPage !== current.page) {
      await loadFolios(tipo, plan, newPage);
    }
  };

  // Ir a página específica desde input
  const handleGoToPage = async (tipo, plan) => {
    const key = `${tipo}_${plan}`;
    const pageNum = parseInt(goToPageInput[key]) || 1;
    await goToPage(tipo, plan, pageNum);
    setGoToPageInput(prev => ({ ...prev, [key]: '' }));
  };

  // Copiar folios de la página actual
  const copyCurrentPage = (key) => {
    const data = foliosData[key];
    if (!data || !data.folios.length) return;

    const text = data.folios.join(', ');
    navigator.clipboard.writeText(text).then(() => {
      setMensaje({ type: 'success', text: `${data.folios.length} folios copiados al portapapeles` });
      setTimeout(() => setMensaje(null), 2000);
    }).catch(() => {
      setMensaje({ type: 'danger', text: 'Error al copiar' });
    });
  };

  // Exportar todos los folios a CSV
  const exportCSV = async (tipo, plan) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `/api/objetos/mkt/stock/folios/export?tipo=${tipo}&plan=${plan}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error('Error al exportar');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_${tipo}_${plan}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setMensaje({ type: 'success', text: 'CSV exportado correctamente' });
      setTimeout(() => setMensaje(null), 2000);
    } catch (err) {
      console.error('Error exportando CSV:', err);
      setMensaje({ type: 'danger', text: err.message });
    }
  };

  if (loading && !stock) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Cargando stock...</span>
        </div>
        <p className="mt-3 text-muted">Cargando inventario...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  if (!stock || stock.total === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-inbox display-1 text-muted"></i>
        <h5 className="mt-3 text-muted">Sin Stock</h5>
        <p className="text-muted">No hay tags impresos disponibles en inventario</p>
      </div>
    );
  }

  // Agrupar categorías por tipo
  const categoriasPorTipo = {};
  Object.entries(stock.categorias).forEach(([key, cat]) => {
    const tipo = cat.tipo;
    if (!categoriasPorTipo[tipo]) {
      categoriasPorTipo[tipo] = [];
    }
    categoriasPorTipo[tipo].push({ key, ...cat });
  });

  // Iconos por tipo
  const iconosTipo = {
    mascota: 'bi-heart',
    llavero: 'bi-key',
    documento: 'bi-file-text',
    coleccionable: 'bi-gem'
  };

  return (
    <div className="stock-tab">
      {/* Header con total y acciones */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            <i className="bi bi-box-seam me-2 text-primary"></i>
            Stock por Tipo y Plan
          </h5>
          <p className="text-muted mb-0">
            <strong>{stock.total}</strong> tags impresos en inventario
          </p>
        </div>

        <button
          className="btn btn-outline-primary"
          onClick={loadStock}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      {/* Mensaje de feedback */}
      {mensaje && (
        <div className={`alert alert-${mensaje.type} alert-dismissible fade show`} role="alert">
          {mensaje.text}
          <button type="button" className="btn-close" onClick={() => setMensaje(null)}></button>
        </div>
      )}

      {/* Tabla de categorías */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Tipo y Plan</th>
                <th className="text-center">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categoriasPorTipo).map(([tipo, categorias]) => (
                <React.Fragment key={tipo}>
                  {/* Header de tipo */}
                  <tr className="table-secondary">
                    <td colSpan="3">
                      <strong>
                        <i className={`bi ${iconosTipo[tipo] || 'bi-tag'} me-2`}></i>
                        {tipo.charAt(0).toUpperCase() + tipo.slice(1)}s
                      </strong>
                    </td>
                  </tr>

                  {/* Categorías de este tipo */}
                  {categorias.map((cat) => {
                    const isExpanded = expandedCategory === cat.key;
                    const folios = foliosData[cat.key];

                    return (
                      <React.Fragment key={cat.key}>
                        <tr
                          className={`stock-category ${isExpanded ? 'table-active' : ''}`}
                          onClick={() => toggleCategory(cat.key, cat.tipo, cat.plan)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="text-center">
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </td>
                          <td>
                            <span className={`badge ${cat.plan === 'premium' ? 'bg-warning text-dark' : cat.plan === 'standard' ? 'bg-info' : 'bg-secondary'} me-2`}>
                              {cat.plan}
                            </span>
                            {tipo.charAt(0).toUpperCase() + tipo.slice(1)} {cat.plan.charAt(0).toUpperCase() + cat.plan.slice(1)}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-primary rounded-pill">
                              {cat.cantidad}
                            </span>
                          </td>
                        </tr>

                        {/* Panel expandido con folios */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="3" className="p-0 bg-light">
                              <div className="p-3">
                                {loadingFolios && !folios ? (
                                  <div className="text-center py-3">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                      <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <span className="ms-2 text-muted">Cargando folios...</span>
                                  </div>
                                ) : folios ? (
                                  <>
                                    {/* Textarea con folios */}
                                    <textarea
                                      className="form-control mb-3"
                                      rows="4"
                                      readOnly
                                      value={folios.folios.join(', ')}
                                      style={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem',
                                        backgroundColor: '#fff',
                                        resize: 'none'
                                      }}
                                    />

                                    {/* Controles de paginación */}
                                    {folios.totalPages > 1 && (
                                      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            goToPage(cat.tipo, cat.plan, folios.page - 1);
                                          }}
                                          disabled={folios.page <= 1 || loadingFolios}
                                        >
                                          <i className="bi bi-chevron-left"></i> Anterior
                                        </button>

                                        <span className="text-muted">
                                          Página <strong>{folios.page}</strong> de <strong>{folios.totalPages}</strong>
                                        </span>

                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            goToPage(cat.tipo, cat.plan, folios.page + 1);
                                          }}
                                          disabled={folios.page >= folios.totalPages || loadingFolios}
                                        >
                                          Siguiente <i className="bi bi-chevron-right"></i>
                                        </button>

                                        <div className="d-flex align-items-center gap-1 ms-2">
                                          <span className="text-muted">Ir a:</span>
                                          <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            style={{ width: '60px' }}
                                            min="1"
                                            max={folios.totalPages}
                                            value={goToPageInput[cat.key] || ''}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              setGoToPageInput(prev => ({
                                                ...prev,
                                                [cat.key]: e.target.value
                                              }));
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.stopPropagation();
                                                handleGoToPage(cat.tipo, cat.plan);
                                              }
                                            }}
                                            placeholder="#"
                                          />
                                          <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleGoToPage(cat.tipo, cat.plan);
                                            }}
                                            disabled={loadingFolios}
                                          >
                                            Ir
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Botones de acción */}
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyCurrentPage(cat.key);
                                        }}
                                      >
                                        <i className="bi bi-clipboard me-1"></i>
                                        Copiar página ({folios.folios.length})
                                      </button>

                                      <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          exportCSV(cat.tipo, cat.plan);
                                        }}
                                      >
                                        <i className="bi bi-download me-1"></i>
                                        Exportar todos CSV ({folios.total})
                                      </button>
                                    </div>

                                    {/* Info de paginación */}
                                    <div className="text-muted small mt-2">
                                      Mostrando {((folios.page - 1) * folios.pageSize) + 1}-
                                      {Math.min(folios.page * folios.pageSize, folios.total)} de {folios.total} folios
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-center text-muted py-3">
                                    Sin folios disponibles
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StockTab;
